import { useEffect, useState } from 'react'
import { useCopy } from '@/i18n/useCopy'
import type { Copy } from '@/i18n/en'
import { useSessionStore } from '@/store/sessionStore'
import type { SessionState } from '@/voice/types'

/**
 * How long a wait may run before the orb alone stops being enough.
 *
 * A turn on Namu's models is recognise, think, synthesise: about 1.5 seconds
 * to first audio when the containers are warm, and worse often enough that
 * three is not remarkable. Five is. Below this the orb is the whole interface
 * and there is nothing to add.
 */
export const SLOW_TURN_MS = 5000

/**
 * And how long before the wait is almost certainly a cold start.
 *
 * Modal scales to zero and comes back in 72 to 132 seconds. `warm.ts` works to
 * stop that happening and mostly does, but when it has not, somebody is
 * looking at a silent screen for over a minute. Twenty seconds is well past
 * any warm turn and well short of the wait it is explaining, which leaves room
 * to say so while it still helps.
 */
export const COLD_START_MS = 20000

/**
 * The states where the person is waiting on something that has not arrived.
 *
 * Both of them are silences with work behind them: `connecting` before the
 * session exists, `processing` between the end of a question and the first
 * word of the answer. Listening is not here, because a person being listened
 * to is not waiting, and neither is `requesting_permission`, where the browser
 * is already showing its own prompt.
 */
const WAITING_STATES: ReadonlySet<SessionState> = new Set<SessionState>([
  'connecting',
  'processing',
])

type Stage = 'brief' | 'slow' | 'cold'

/**
 * The words for a wait that has gone on too long, or an empty string.
 *
 * This lives in a hook rather than in the component that shows it because two
 * places need the same sentence: `Waiting` paints it on the stage and
 * `LiveRegions` speaks it. They keep separate timers, since each starts its own
 * when it mounts, but both read one store and one pair of thresholds, so they
 * can only ever differ by the render that notices a wait began. Writing the
 * rule out twice is what would let them disagree about whether a turn is merely
 * slow or actually cold.
 */
export function useWaitingNote(): string {
  const copy = useCopy()
  const state = useSessionStore((store) => store.state)
  const held = useSessionStore((store) => store.held)

  // Hold is a wait the person chose, and they do not need it explained.
  const waiting = WAITING_STATES.has(state) && !held
  const [stage, setStage] = useState<Stage>('brief')

  /*
   * The reset happens during the render that notices the wait started or
   * ended, rather than in an effect afterwards. React re-runs this hook
   * before touching the DOM, so nothing renders with the previous wait's
   * stage; doing it in an effect would paint "still thinking" for one frame at
   * the start of the next turn, which is a lie told fast enough to see.
   */
  const [waitingBefore, setWaitingBefore] = useState(waiting)
  if (waitingBefore !== waiting) {
    setWaitingBefore(waiting)
    setStage('brief')
  }

  /*
   * Keyed on whether a wait is running, never on which state it is in. A turn
   * that goes connecting then processing is one wait from where the person is
   * sitting, and restarting the clock on the internal transition would keep
   * resetting a wait that never actually stopped.
   */
  useEffect(() => {
    if (!waiting) return
    const slow = setTimeout(() => setStage('slow'), SLOW_TURN_MS)
    const cold = setTimeout(() => setStage('cold'), COLD_START_MS)
    return () => {
      clearTimeout(slow)
      clearTimeout(cold)
    }
  }, [waiting])

  return noteFor(stage, state, copy.ui.waiting)
}

/**
 * Which line, given how long it has been and what is being waited on.
 *
 * A cold start explains itself the same way whichever state it happens in:
 * past twenty seconds the interesting fact is not that Namu is thinking, it is
 * that the machine behind Namu was asleep.
 */
function noteFor(stage: Stage, state: SessionState, copy: Copy['ui']['waiting']): string {
  if (stage === 'brief') return ''
  if (stage === 'cold') return copy.waking
  return state === 'connecting' ? copy.connecting : copy.thinking
}
